/**
 * The order a person dragged a table's columns and rows into (DOP-649).
 *
 * The financial summary keeps it in the version's conventions,
 * `financial_columns` (columns by name) and `financial_rows` (rows by
 * figure key), each a JSON list. Whatever the order does not name keeps its
 * own place after the ones it does, so a column a later run adds still shows.
 */

export const FINANCIAL_COLUMNS = "financial_columns";
export const FINANCIAL_ROWS = "financial_rows";

/** A saved order, or [] when there is none or it does not read as a list of names. */
export function orderSetting(conventions: Record<string, string> | null | undefined, key: string): string[] {
	const raw = conventions?.[key];
	if (!raw) return [];
	try {
		const value: unknown = JSON.parse(raw);
		return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
	} catch {
		return [];
	}
}

/** `items` in the saved order: those it names first, in its order; the rest after, as they came. */
export function inSavedOrder<T>(items: T[], order: string[] | null | undefined, name: (item: T) => string): T[] {
	if (!order || order.length === 0) return items;
	const place = new Map(order.map((value, index) => [value, index]));
	return items
		.map((item, index) => ({ item, index, at: place.get(name(item)) ?? order.length }))
		.sort((a, b) => a.at - b.at || a.index - b.index)
		.map(({ item }) => item);
}

/** The list with the item at `from` moved to `to`. */
export function moved<T>(items: T[], from: number, to: number): T[] {
	if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
	const next = [...items];
	const [item] = next.splice(from, 1);
	next.splice(to, 0, item);
	return next;
}

/** The one move that turns `before` into `after`, as [from, to]; null when they are the same. */
export function singleMove(before: string[], after: string[]): [number, number] | null {
	let first = 0;
	while (first < before.length && before[first] === after[first]) first += 1;
	if (first === before.length) return null;
	let last = before.length - 1;
	while (last > first && before[last] === after[last]) last -= 1;
	return after[last] === before[first] ? [first, last] : [last, first];
}
